import type { FetchResult } from '@apollo/client';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { PatientRecord } from '@models/patient';

import {
  useGetPatientRecordQuery,
  useGetPatientRecordWithAttachmentsQuery,
  useGetAllPatientsQuery,
  useUpdatePatientRecordMutation,
} from 'generated/fhir-patient.generated';
import type {
  UpdatePatientRecordMutation,
  UpdatePatientRecordMutationVariables,
} from 'generated/fhir-patient.generated';

export const useGetAllFHIRPatients = () => {
  const {
    state: { isCurator },
  } = useSession();
  const response = useGetAllPatientsQuery({
    variables: { input: { count: 1500, page: 1 } },
    skip: !isCurator,
  });
  const { data } = response;
  const patients = (data?.getAllPatients.data ?? []).map(
    (p) => new PatientRecord(p)
  );
  return { ...response, data: patients };
};

export const useGetFHIRPatient = () => {
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetPatientRecordQuery({
    variables: {
      input: {
        patient_id,
      },
    },
    skip: !patient_id,
  });
  const patient = response.data?.getPatientRecord;
  return { ...response, data: patient ? new PatientRecord(patient) : null };
};

export const useGetFHIRPatientWithAttachments = () => {
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetPatientRecordWithAttachmentsQuery({
    variables: {
      input: {
        patient_id,
      },
    },
    skip: !patient_id,
  });
  const patient = response.data?.getPatientRecord;
  return { ...response, data: patient ? new PatientRecord(patient) : null };
};

export function useUpdateFHIRPatient(): [
  (
    params: UpdatePatientRecordMutationVariables
  ) => Promise<FetchResult<UpdatePatientRecordMutation>>,
  boolean,
] {
  const { handleError } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updatePatientRecord',
  });
  const [mutate, { loading: baseLoading }] = useUpdatePatientRecordMutation();
  const loading = baseLoading;

  async function updateFHIRPatient(
    params: UpdatePatientRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    // Mutations run with errorPolicy 'all', so a failed update resolves rather
    // than throwing. Without this the failure is silent and any later call in
    // the same save handler gets blamed for it.
    const { errors } = response;
    if (errors) {
      handleError(errors);
    }
    return response;
  }
  return [updateFHIRPatient, loading];
}
